import { action, makeObservable, observable, runInAction } from "mobx";
import PaymentLinksAPI from "../api/PaymentLinksAPI";
import { FilterValue } from "../models/Filters/FilterValue";
import { PaginatedAPIResponse } from "../models/NetworkResponse";
import {
  PaymentLinkFilters,
  PaymentLinkListItem,
  parsePaymentLinkStatusParam,
} from "../models/PaymentLinks/PaymentLinks";
import { SortType } from "../models/SortType";

const DEFAULT_PARAMS: PaymentLinkFilters = {
  page_no: 1,
  limit: 30,
  sort_by: "created_date",
  sort_type: SortType.DESC,
};

export class PaymentLinkStore {
  paymentLinks: PaymentLinkListItem[];
  paymentLinksAPI: PaymentLinksAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  isLoading: boolean;
  isExporting: boolean;

  params: PaymentLinkFilters;
  draftStartDate?: string;
  draftEndDate?: string;
  draftStatus: FilterValue[];
  searchTerm: string;
  isSearchExpanded: boolean;

  constructor(searchParams?: URLSearchParams) {
    this.paymentLinks = [];
    this.paymentLinksAPI = new PaymentLinksAPI();
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.isLoading = false;
    this.isExporting = false;
    this.params = { ...DEFAULT_PARAMS };
    this.draftStatus = [];
    this.searchTerm = "";
    this.isSearchExpanded = false;

    if (searchParams) {
      this.initFromSearchParams(searchParams);
    }

    makeObservable(this, {
      paymentLinks: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      isLoading: observable,
      isExporting: observable,
      params: observable,
      draftStartDate: observable,
      draftEndDate: observable,
      draftStatus: observable,
      searchTerm: observable,
      isSearchExpanded: observable,
      initFromSearchParams: action,
      handleSearchBar: action,
      handleSearchSubmit: action,
      handleApply: action,
      resetFilters: action,
      setPage: action,
      setRowsPerPage: action,
      setSort: action,
      setDraftDateRange: action,
      setDraftStatus: action,
      toggleSearchExpanded: action,
      getPaymentLinks: action,
      exportPaymentLinksCSV: action,
    });
  }

  initFromSearchParams = (searchParams: URLSearchParams) => {
    this.params = {
      page_no: searchParams.get("page_no")
        ? parseInt(searchParams.get("page_no") as string, 10)
        : DEFAULT_PARAMS.page_no,
      limit: searchParams.get("limit")
        ? parseInt(searchParams.get("limit") as string, 10)
        : DEFAULT_PARAMS.limit,
      order_id: searchParams.get("order_id") || undefined,
      status: searchParams.get("status") || undefined,
      start_date: searchParams.get("start_date") || undefined,
      end_date: searchParams.get("end_date") || undefined,
      sort_by: searchParams.get("sort_by") || DEFAULT_PARAMS.sort_by,
      sort_type:
        searchParams.get("sort_type") === SortType.ASC
          ? SortType.ASC
          : SortType.DESC,
    };
    this.draftStartDate = this.params.start_date;
    this.draftEndDate = this.params.end_date;
    this.draftStatus = parsePaymentLinkStatusParam(searchParams.get("status"));
    this.searchTerm = searchParams.get("order_id") || "";
  };

  private updateParams = (partial: Partial<PaymentLinkFilters>) => {
    this.params = { ...this.params, ...partial };
  };

  handleSearchBar = (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      this.searchTerm = searchText;
      return;
    }
    this.searchTerm = "";
    this.updateParams({ order_id: undefined, page_no: 1 });
  };

  handleSearchSubmit = () => {
    const trimmed = this.searchTerm.trim();
    this.searchTerm = trimmed;
    this.updateParams({ order_id: trimmed || undefined, page_no: 1 });
  };

  handleApply = () => {
    const trimmed = this.searchTerm.trim();
    const statusParam =
      this.draftStatus.length > 0
        ? this.draftStatus.map((item) => item.id).join(",")
        : undefined;
    this.searchTerm = trimmed;
    this.updateParams({
      order_id: trimmed || undefined,
      status: statusParam,
      start_date: this.draftStartDate,
      end_date: this.draftEndDate,
      page_no: 1,
    });
  };

  resetFilters = () => {
    this.searchTerm = "";
    this.draftStatus = [];
    this.draftStartDate = undefined;
    this.draftEndDate = undefined;
    this.params = { ...DEFAULT_PARAMS };
  };

  setPage = (page_no: number) => {
    this.updateParams({ page_no });
  };

  setRowsPerPage = (limit: number) => {
    this.updateParams({ limit, page_no: 1 });
  };

  setSort = (sort_by: string, sort_type: SortType) => {
    this.updateParams({ page_no: 1, sort_by, sort_type });
  };

  setDraftDateRange = (start_date?: string, end_date?: string) => {
    this.draftStartDate = start_date;
    this.draftEndDate = end_date;
  };

  setDraftStatus = (status: FilterValue[]) => {
    this.draftStatus = status;
  };

  toggleSearchExpanded = () => {
    this.isSearchExpanded = !this.isSearchExpanded;
  };

  getPaymentLinks = async () => {
    try {
      this.isLoading = true;
      const response = (await this.paymentLinksAPI.getPaymentLinks(
        this.params,
      )) as PaginatedAPIResponse<PaymentLinkListItem>;
      runInAction(() => {
        this.paymentLinks = response.list ?? [];
        this.limit = response.limit ?? 0;
        this.total_pages = response.total_pages ?? 0;
        this.total_count = response.total_count ?? 0;
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
      throw error;
    }
  };

  exportPaymentLinksCSV = async () => {
    try {
      this.isExporting = true;
      const response = await this.paymentLinksAPI.downloadPaymentLinksCSV({
        order_id: this.params.order_id,
        status: this.params.status,
        start_date: this.params.start_date,
        end_date: this.params.end_date,
        sort_by: this.params.sort_by,
        sort_type: this.params.sort_type,
      });
      runInAction(() => {
        this.isExporting = false;
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.isExporting = false;
      });
      throw error;
    }
  };
}
